<script lang="ts" setup>
import { onMounted, ref, Ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { helpers, numeric, required } from '@vuelidate/validators';
import AutoComplete from 'primevue/autocomplete';

import { EMIT } from '@/enum';
import { useCitiesStore, useStatesStore, useTenantStore } from '@/store';
import { storeToRefs } from 'pinia';

//phone number regex helper validator
const number = helpers.regex(/^[0-9]{3}-[0-9]{3}-[0-9]{4}$/);
const zipcode = helpers.regex(/^\d{5}(-\d{4})?$/);

const emit = defineEmits([EMIT.NEXT, EMIT.BACK, EMIT.SAVE]);
const stateStore = useStatesStore();
const citiesStore = useCitiesStore();
const tenantStore = useTenantStore();

/**
 * The data ref object for newTenant so you can come back to the newTenant.
 */
const { newTenant } = storeToRefs(tenantStore);

/**
 * The suggested cities after running a search.
 */
const cities = ref([]);

/**
 * The required validation rules.
 */
const rules = {
  name: { required },
  street: { required },
  city_name: { required },
  state_id: { required },
  zip: { required, numeric, zipcode: helpers.withMessage('Zipcode must be valid', zipcode) },
  phone: { required, number: helpers.withMessage('Phone must be in format 123-456-7890', number) },
};

/**
 * Initialize the vuelidate instance.
 */
const v$ = useVuelidate(rules, newTenant);

/**
 * Determines whether or not the form has been submitted.
 */
const submitted = ref<boolean>(false);

onMounted(() => {
  init();
});

const init = async () => await stateStore.getStates();

/**
 * Handle the client side validation, followed by saving the
 * information for the new user account.
 */
const onSave = () => {
  if (v$.value.$invalid) return;
  emit(EMIT.NEXT);
};

/**
 * Search for a city with the provided city name, state and country identifier.
 * Also, Attempt to pre-populate the state based on the provided city name.
 */
const onCitySearch = async () => {
  if (newTenant.value.state_id && newTenant.value.city_name) {
    const params = {
      country_id: 1,
      search: newTenant.value.city_name,
    };

    citiesStore.searchCities(params).then((response) => (cities.value = response.data.data));
  } else {
    const response = await citiesStore.searchCities({ search: newTenant.value.city_name });
    const stateIds = response.data.data.map((item) => item);

    if (stateIds.length === 1) {
      newTenant.value.state_id = stateIds[0].state_id;
      newTenant.value.city_id = stateIds[0].id;
    }
  }
};

/**
 * Update the city, state and country identifiers once a suggested
 * city has been selected.
 */
const onCityUpdate = (event: Ref) => {
  newTenant.value.city_id = event.value.id;
  newTenant.value.city_name = event.value.name;
  newTenant.value.state_id = event.value.state_id;
};
</script>

<template>
  <div class="field mt-3">
    <label class="required" for="name">Tenant Name</label>
    <InputText
      id="name"
      v-model="v$.name.$model"
      type="text"
      placeholder="Tenant Name"
      :disabled="submitted"
      class="w-full"
    />
    <div v-for="error of v$.name.$errors" :key="error.$uid" class="input-errors">
      <small>{{ error.$message }}</small>
    </div>
  </div>
  <div class="field mt-3">
    <label class="required" for="street">Address</label>
    <InputText
      id="street"
      v-model="v$.street.$model"
      type="text"
      placeholder="Tenant Address"
      :disabled="submitted"
      class="w-full"
    />
    <div v-for="error of v$.street.$errors" :key="error.$uid" class="input-errors">
      <small>{{ error.$message }}</small>
    </div>
  </div>
  <div class="flex flex-wrap gap-3 mt-3">
    <div class="field flex-1">
      <label class="required" for="city">City</label>
      <AutoComplete
        id="city"
        v-model="v$.city_name.$model"
        class="city_name mr-2"
        field="name"
        :min-length="2"
        placeholder="City"
        :suggestions="cities"
        @complete="onCitySearch"
        @item-select="onCityUpdate"
      />
      <div v-for="error of v$.city_name.$errors" :key="error.$uid" class="input-errors">
        <small>{{ error.$message }}</small>
      </div>
    </div>

    <div class="field flex-1">
      <label class="required" for="state_id">State</label>
      <Dropdown
        id="state_id"
        v-model="v$.state_id.$model"
        :options="stateStore.states"
        option-label="name"
        option-value="id"
        placeholder="State"
        class="w-full"
      />
    </div>
    <div class="field flex-1">
      <label class="required" for="state_id">Zipcode</label>
      <InputText
        id="zip"
        v-model="v$.zip.$model"
        type="text"
        placeholder="Zipcode"
        :disabled="submitted"
        class="w-full"
      />
      <div v-for="error of v$.zip.$errors" :key="error.$uid" class="input-errors">
        <small>{{ error.$message }}</small>
      </div>
    </div>
  </div>
  <div class="field">
    <label class="required" for="phone">Phone</label>
    <InputText
      id="phone"
      v-model="v$.phone.$model"
      v-maska="'###-###-####'"
      type="text"
      placeholder="Phone"
      :disabled="submitted"
      class="w-full"
    />
    <div v-for="error of v$.phone.$errors" :key="error.$uid" class="input-errors">
      <small>{{ error.$message }}</small>
    </div>
  </div>
  <div class="dialog-footer flex justify-content-end">
    <AppButton
      icon-left="arrow_forward"
      type="secondary"
      text="Next"
      class="admin-button"
      :disabled="v$.$invalid || submitted"
      @click="onSave"
    />
  </div>
</template>

<style lang="scss" scoped>
.input-errors {
  color: $errorColor;
}
</style>
